import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SharedPageLayout from '../components/SharedPageLayout';
import MainNavigation from '../components/MainNavigation';
import BrandName from '../components/BrandName';
import Footer from '../components/Footer';
import ThreeDPreview from '../components/ThreeDPreview';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Upload, FileText, Package, Eye, Edit, Trash2, CheckCircle, XCircle, Clock, DollarSign, ArrowLeft, Store, Pencil, Check, X as XIcon, Copy, Loader2, History, Info } from 'lucide-react';
import { trackDesignerApplication, trackDesignUpload } from '../utils/analytics';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;


export const DesignerApplyRoute = () => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [formData, setFormData] = useState({
    designer_name: '',
    display_name: '',
    bio: '',
    portfolio_links: ['']
  });
  const [sampleFiles, setSampleFiles] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const addPortfolioLink = () => {
    setFormData(prev => ({
      ...prev,
      portfolio_links: [...prev.portfolio_links, '']
    }));
  };

  const removePortfolioLink = (index) => {
    setFormData(prev => ({
      ...prev,
      portfolio_links: prev.portfolio_links.filter((_, i) => i !== index)
    }));
  };

  const updatePortfolioLink = (index, value) => {
    setFormData(prev => ({
      ...prev,
      portfolio_links: prev.portfolio_links.map((link, i) => i === index ? value : link)
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setSampleFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setSubmitStatus({ type: 'error', message: 'Please sign in to apply as a designer.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Use proper backend URL from environment variable
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      const applicationData = {
        designer_name: formData.designer_name,
        bio: formData.bio,
        portfolio_links: formData.portfolio_links.filter(link => link.trim() !== '')
      };

      const response = await fetch(`${API_BASE}/api/marketplace/apply-designer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(applicationData)
      });

      // Get raw text first to debug JSON parsing issues
      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Raw response:', responseText);
        console.error('Response status:', response.status);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));
        throw new Error(`Server returned invalid response: ${responseText.substring(0, 100)}`);
      }

      if (response.ok) {
        // Handle different application statuses
        if (result.status === 'pending') {
          // User already has a pending application - show info message
          setSubmitStatus({
            type: 'info',
            message: result.message
          });
        } else if (result.status === 'approved') {
          // User's application was already approved
          setSubmitStatus({
            type: 'success',
            message: result.message
          });
        } else {
          // New application submitted successfully
          trackDesignerApplication();
          setSubmitStatus({
            type: 'success',
            message: 'Application submitted successfully! We\'ll review your application and get back to you within 2-3 business days.'
          });
          setFormData({ designer_name: '', display_name: '', bio: '', portfolio_links: [''] });
          setSampleFiles([]);
        }
      } else {
        throw new Error(result.detail || 'Application submission failed');
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to submit application. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SharedPageLayout
      navigateHome={() => navigate('/')}
      navigateToCart={() => navigate('/cart')}
      navigateToProfile={() => navigate('/profile')}
      cartCount={0}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Apply to Become a Designer</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Join our marketplace and sell your 3D designs to customers worldwide. 
            Professional platform with global reach and secure payments.
          </p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-[#0007b5]">Designer Application</CardTitle>
            <CardDescription>
              Tell us about yourself and your 3D design experience. We'll review your application within 2-3 business days.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="designer_name">Designer Name *</Label>
                <Input
                  id="designer_name"
                  type="text"
                  placeholder="Your professional name or studio name"
                  value={formData.designer_name}
                  onChange={(e) => setFormData(prev => ({...prev, designer_name: e.target.value}))}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="display_name">Display Name (Optional)</Label>
                <Input
                  id="display_name"
                  type="text"
                  placeholder="Business name or brand name for marketplace listings"
                  value={formData.display_name || ''}
                  onChange={(e) => setFormData(prev => ({...prev, display_name: e.target.value}))}
                  className="mt-2"
                />
                <p className="text-sm text-slate-500 mt-1">
                  Optional: Use a business name or brand that will appear on your marketplace listings. Leave blank to use your designer name.
                </p>
              </div>

              <div>
                <Label htmlFor="bio">Biography *</Label>
                <Textarea
                  id="bio"
                  placeholder="Tell us about your 3D design experience, specializations, and background..."
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({...prev, bio: e.target.value}))}
                  required
                  rows={6}
                  className="mt-2"
                />
                <p className="text-sm text-slate-500 mt-1">
                  Include your experience, specializations, and what makes your designs unique.
                </p>
              </div>

              <div>
                <Label>Portfolio Links (Optional)</Label>
                <div className="space-y-3 mt-2">
                  {formData.portfolio_links.map((link, index) => (
                    <div key={index} className="flex space-x-2">
                      <Input
                        type="url"
                        placeholder="https://your-portfolio.com or https://github.com/username"
                        value={link}
                        onChange={(e) => updatePortfolioLink(index, e.target.value)}
                        className="flex-1"
                      />
                      {formData.portfolio_links.length > 1 && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => removePortfolioLink(index)}
                          className="px-3"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={addPortfolioLink}
                    className="w-full"
                  >
                    Add Another Portfolio Link
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="sample_files">Sample Files (Optional)</Label>
                <Input
                  id="sample_files"
                  type="file"
                  accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                  onChange={handleFileUpload}
                  multiple
                  className="mt-2"
                />
                <p className="text-sm text-slate-500 mt-1">
                  Upload 1-3 sample CAD files to showcase your work. Max 50MB per file.
                </p>
                {sampleFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {sampleFiles.map((file, index) => (
                      <Badge key={index} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        ✓ {file.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-3">Designer Benefits</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex items-center">
                    <span>✓ Global marketplace reach</span>
                  </div>
                  <div className="flex items-center">
                    <span>✓ Secure payment processing</span>
                  </div>
                  <div className="flex items-center">
                    <span>✓ Marketing support for your designs</span>
                  </div>
                  <div className="pt-2 mt-3 border-t border-blue-200">
                    <p>Revenue details will be shared upon application approval.</p>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 border border-slate-200 rounded-lg bg-slate-50">
                <input
                  type="checkbox"
                  id="terms-agreement"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 text-[#0007b5] focus:ring-[#0007b5] border-slate-300 rounded"
                  required
                />
                <label htmlFor="terms-agreement" className="text-sm text-slate-700">
                  I have read and agree to the{' '}
                  <a
                    href="/designer/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0007b5] hover:underline font-medium"
                  >
                    Designer Terms and Conditions
                  </a>
                  {' '}*
                </label>
              </div>

              {submitStatus && (
                <div className={`rounded-lg p-4 ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : submitStatus.type === 'info'
                    ? 'bg-blue-50 text-blue-800 border border-blue-200'
                    : submitStatus.type === 'warning'
                    ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <Button 
                type="submit" 
                disabled={isSubmitting || !user || !agreedToTerms}
                className="w-full bg-[#0007b5] hover:bg-[#000690] py-3 text-lg font-semibold"
              >
                {isSubmitting ? 'Submitting Application...' : 'Submit Designer Application'}
              </Button>

              {!user && (
                <p className="text-center text-slate-600">
                  Please <button onClick={() => navigate('/')} className="text-[#0007b5] hover:underline">sign in</button> to submit your application.
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </SharedPageLayout>
  );
};


export const DesignerDashboardRoute = () => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Storefront URL editing state ---
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState('');
  const [usernameCheck, setUsernameCheck] = useState({ status: 'idle', reason: '' }); // idle|checking|available|taken|invalid
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [usernameHistory, setUsernameHistory] = useState(null); // { history, can_change_now, next_change_available_at, ttl_days, cooldown_days }

  // Fetch username history + rate-limit metadata
  const fetchUsernameHistory = async () => {
    if (!user) return;
    try {
      const token = await getIdToken();
      const res = await fetch(`${BACKEND_URL}/api/creator/my-username-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUsernameHistory(data);
      }
    } catch (_) {
      // non-fatal; UI will simply hide the history section
    }
  };

  useEffect(() => {
    fetchUsernameHistory();
  }, [user]);

  // Debounced availability check
  useEffect(() => {
    if (!isEditingUrl) return;
    const raw = (usernameDraft || '').trim().toLowerCase();

    // No change vs current username -> treat as available, no API call
    if (dashboardData?.creator_username && raw === dashboardData.creator_username) {
      setUsernameCheck({ status: 'idle', reason: '' });
      return;
    }
    if (!raw) {
      setUsernameCheck({ status: 'idle', reason: '' });
      return;
    }
    // Client-side validation first
    if (raw.length < 3) {
      setUsernameCheck({ status: 'invalid', reason: 'Must be at least 3 characters' });
      return;
    }
    if (raw.length > 30) {
      setUsernameCheck({ status: 'invalid', reason: 'Must be 30 characters or fewer' });
      return;
    }
    if (!/^[a-z0-9_]+$/.test(raw)) {
      setUsernameCheck({ status: 'invalid', reason: 'Only lowercase letters, numbers, underscores' });
      return;
    }

    let cancelled = false;
    setUsernameCheck({ status: 'checking', reason: '' });
    const timer = setTimeout(async () => {
      try {
        const token = await getIdToken();
        const res = await fetch(
          `${BACKEND_URL}/api/creator/username-available?username=${encodeURIComponent(raw)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (cancelled) return;
        if (data.available) {
          setUsernameCheck({ status: 'available', reason: '' });
        } else {
          setUsernameCheck({
            status: data.reason?.toLowerCase().includes('taken') ? 'taken' : 'invalid',
            reason: data.reason || 'Unavailable',
          });
        }
      } catch (err) {
        if (cancelled) return;
        setUsernameCheck({ status: 'invalid', reason: 'Could not verify availability' });
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [usernameDraft, isEditingUrl]);

  const openUrlEditor = () => {
    setUsernameDraft(dashboardData?.creator_username || '');
    setUsernameError('');
    setUsernameCheck({ status: 'idle', reason: '' });
    setIsEditingUrl(true);
  };

  const cancelUrlEditor = () => {
    setIsEditingUrl(false);
    setUsernameDraft('');
    setUsernameError('');
    setUsernameCheck({ status: 'idle', reason: '' });
  };

  const saveUsername = async () => {
    const normalized = (usernameDraft || '').trim().toLowerCase();
    if (!normalized) return;
    if (normalized === dashboardData?.creator_username) {
      cancelUrlEditor();
      return;
    }
    if (usernameCheck.status !== 'available') return;

    setSavingUsername(true);
    setUsernameError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${BACKEND_URL}/api/creator/my-username`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username: normalized }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 429 returns a structured detail object { message, next_change_available_at }
        if (res.status === 429 && typeof data.detail === 'object') {
          setUsernameError(data.detail.message || 'Rate limit reached');
          // Refresh history so the UI disables the Save button
          fetchUsernameHistory();
        } else {
          setUsernameError(
            (typeof data.detail === 'string' ? data.detail : null) ||
              'Failed to update URL'
          );
        }
        return;
      }
      setDashboardData((prev) => (prev ? { ...prev, creator_username: data.username } : prev));
      setIsEditingUrl(false);
      setUsernameDraft('');
      setUsernameCheck({ status: 'idle', reason: '' });
      // Refresh history panel with new entry + refreshed cooldown
      fetchUsernameHistory();
    } catch (err) {
      setUsernameError('Network error — please try again');
    } finally {
      setSavingUsername(false);
    }
  };

  const copyStorefrontLink = async () => {
    if (!dashboardData?.creator_username) return;
    const url = `${window.location.origin}/creator/${dashboardData.creator_username}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      // no-op; clipboard may be unavailable
    }
  };

  // "in 3 days", "in 2 months", "today" — relative time for cooldown/expiry UI
  const formatRelative = (iso) => {
    if (!iso) return '';
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return '';
    const diffMs = target - Date.now();
    const past = diffMs < 0;
    const abs = Math.abs(diffMs);
    const days = Math.round(abs / (1000 * 60 * 60 * 24));
    if (days === 0) return past ? 'today' : 'later today';
    if (days < 30) {
      const label = `${days} day${days === 1 ? '' : 's'}`;
      return past ? `${label} ago` : `in ${label}`;
    }
    const months = Math.round(days / 30);
    const label = `${months} month${months === 1 ? '' : 's'}`;
    return past ? `${label} ago` : `in ${label}`;
  };

  const cooldownActive = usernameHistory && usernameHistory.can_change_now === false;
  const cooldownMessage = cooldownActive
    ? `You can change your storefront URL again ${formatRelative(
        usernameHistory.next_change_available_at
      )}. Contact support to request an admin override.`
    : '';

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) return;

      try {
        // Use proper backend URL from environment variable
        const API_BASE = BACKEND_URL;
        const token = await getIdToken();

        // Fetch dashboard data
        const dashboardResponse = await fetch(`${API_BASE}/api/marketplace/designer/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (dashboardResponse.ok) {
          const dashboardResult = await dashboardResponse.json();
          setDashboardData(dashboardResult);
        }

        // Fetch designer listings
        const listingsResponse = await fetch(`${API_BASE}/api/marketplace/designer/listings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (listingsResponse.ok) {
          const listingsResult = await listingsResponse.json();
          setListings(listingsResult.listings || []);
        }

      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center p-6">
            <h2 className="text-xl font-semibold mb-4">Access Required</h2>
            <p className="text-gray-600 mb-4">Please sign in to access the designer dashboard.</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MainNavigation 
        onNavigate={(page) => {
          if (page === 'home') navigate('/');
          else if (page === 'designer-dashboard') navigate('/designer/dashboard');
          else navigate(`/${page}`);
        }} 
        currentPage="designer-dashboard" 
        cartCount={0}
      />

      {/* Dashboard Action Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Designer Dashboard</h1>
            </div>
            <Button 
              onClick={() => navigate('/designer/upload')}
              className="bg-[#0007b5] hover:bg-[#000690]"
            >
              <Upload className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Upload Design</span>
              <span className="sm:hidden">Upload</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${dashboardData?.earnings?.total_earnings?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600">$</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Listings</p>
                  <p className="text-2xl font-bold text-[#0007b5]">
                    {dashboardData?.active_listings || 0}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#0007b5]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Downloads</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {dashboardData?.earnings?.total_downloads || 0}
                  </p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Upload className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending Payout</p>
                  <p className="text-2xl font-bold text-orange-600">
                    ${dashboardData?.earnings?.pending_payout?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-600">⏳</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* My Storefront Section */}
        {dashboardData?.creator_username && (
          <Card className="mb-8" data-testid="storefront-card">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4 w-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0007b5] to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Store className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900">My Storefront</h3>

                    {!isEditingUrl ? (
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <p className="text-sm text-gray-500 mr-1">
                          Your public designer page at{' '}
                          <span className="font-medium text-[#0007b5]" data-testid="storefront-url-display">
                            /creator/{dashboardData.creator_username}
                          </span>
                        </p>
                        <button
                          type="button"
                          onClick={openUrlEditor}
                          disabled={cooldownActive}
                          className="inline-flex items-center gap-1.5 rounded-md border border-[#0007b5] text-[#0007b5] bg-white hover:bg-[#0007b5]/5 px-3 py-1.5 text-[13px] font-semibold transition disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-white"
                          data-testid="storefront-url-edit-btn"
                          title={cooldownActive ? cooldownMessage : 'Edit storefront URL'}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit URL
                        </button>
                        <button
                          type="button"
                          onClick={copyStorefrontLink}
                          className="inline-flex items-center gap-1.5 rounded-md border border-gray-400 text-gray-600 bg-white hover:bg-gray-100 hover:border-gray-500 px-3 py-1.5 text-[13px] font-semibold transition"
                          data-testid="storefront-url-copy-btn"
                        >
                          {copiedLink ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-green-600" />
                              <span className="text-green-600">Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" />
                              Copy link
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2 space-y-3" data-testid="storefront-url-edit-form">
                        {/* Policy notes */}
                        <div
                          className="rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2.5 text-xs text-gray-700 space-y-1.5"
                          data-testid="storefront-url-policy-notes"
                        >
                          <div className="flex items-start gap-2">
                            <Info className="w-3.5 h-3.5 text-[#0007b5] mt-0.5 flex-shrink-0" />
                            <p className="font-semibold text-[#0007b5]">
                              Before you rename your storefront
                            </p>
                          </div>
                          <ul className="list-disc pl-6 space-y-1 marker:text-[#0007b5]/60">
                            <li>
                              <span className="font-medium">Limit:</span> you can change
                              your URL <span className="font-medium">once every 30 days</span>.
                              Need another change sooner? Email{' '}
                              <a
                                href="mailto:support@create3dworks.com"
                                className="text-[#0007b5] hover:underline"
                              >
                                support@create3dworks.com
                              </a>{' '}
                              for an admin override.
                            </li>
                            <li>
                              <span className="font-medium">Old-link redirect:</span> your
                              previous URL will auto-redirect to your new one for{' '}
                              <span className="font-medium">6 months</span>, then the old
                              handle is released and becomes available to other designers.
                            </li>
                            <li>
                              <span className="font-medium">What carries over:</span> all
                              your listings, reviews, and sales stay attached to your
                              account — only the public URL changes.
                            </li>
                          </ul>
                        </div>

                        <div className="flex items-stretch gap-2 flex-wrap">
                          <div className="flex items-stretch flex-1 min-w-0 sm:min-w-[260px] border border-gray-300 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-[#0007b5] focus-within:border-[#0007b5]">
                            <span className="inline-flex items-center px-3 text-sm text-gray-500 bg-gray-50 border-r border-gray-200 select-none">
                              /creator/
                            </span>
                            <input
                              type="text"
                              value={usernameDraft}
                              onChange={(e) =>
                                setUsernameDraft(e.target.value.toLowerCase().replace(/\s+/g, ''))
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveUsername();
                                if (e.key === 'Escape') cancelUrlEditor();
                              }}
                              placeholder="your_handle"
                              maxLength={30}
                              autoFocus
                              className="flex-1 px-3 py-2 text-sm focus:outline-none"
                              data-testid="storefront-url-input"
                              disabled={savingUsername}
                            />
                          </div>
                          <Button
                            onClick={saveUsername}
                            disabled={
                              savingUsername ||
                              usernameCheck.status !== 'available' ||
                              !usernameDraft.trim()
                            }
                            className="bg-[#0007b5] hover:bg-[#000690]"
                            data-testid="storefront-url-save-btn"
                          >
                            {savingUsername ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Saving
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Save
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={cancelUrlEditor}
                            disabled={savingUsername}
                            data-testid="storefront-url-cancel-btn"
                          >
                            <XIcon className="w-4 h-4 mr-2" />
                            Cancel
                          </Button>
                        </div>

                        <div className="text-xs min-h-[18px]" data-testid="storefront-url-status">
                          {usernameCheck.status === 'checking' && (
                            <span className="text-gray-500 inline-flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Checking availability…
                            </span>
                          )}
                          {usernameCheck.status === 'available' && (
                            <span className="text-green-600 inline-flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Available
                            </span>
                          )}
                          {usernameCheck.status === 'taken' && (
                            <span className="text-red-600 inline-flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              {usernameCheck.reason || 'Already taken'}
                            </span>
                          )}
                          {usernameCheck.status === 'invalid' && (
                            <span className="text-amber-600 inline-flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              {usernameCheck.reason}
                            </span>
                          )}
                          {usernameCheck.status === 'idle' && (
                            <span className="text-gray-400">
                              3–30 characters · lowercase letters, numbers, underscores
                            </span>
                          )}
                        </div>

                        {usernameError && (
                          <p className="text-xs text-red-600" data-testid="storefront-url-error">
                            {usernameError}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!isEditingUrl && (
                  <div className="flex gap-3 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/creator/${dashboardData.creator_username}`)}
                      className="flex-1 sm:flex-none"
                      data-testid="storefront-view-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Storefront
                    </Button>
                    <Button
                      onClick={() => navigate(`/creator/${dashboardData.creator_username}?edit=true`)}
                      className="flex-1 sm:flex-none bg-[#0007b5] hover:bg-[#000690]"
                      data-testid="storefront-edit-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Storefront
                    </Button>
                  </div>
                )}
              </div>

              {/* Cooldown banner */}
              {cooldownActive && !isEditingUrl && (
                <div
                  className="mt-5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                  data-testid="storefront-url-cooldown"
                >
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{cooldownMessage}</span>
                </div>
              )}

              {/* Redirect history */}
              {usernameHistory && usernameHistory.history && usernameHistory.history.length > 0 && (
                <div
                  className="mt-5 pt-5 border-t border-gray-100"
                  data-testid="storefront-url-history"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <History className="w-4 h-4 text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-700">Redirects from previous URLs</h4>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Old links keep working for {usernameHistory.ttl_days} days after a rename.
                  </p>
                  <ul className="space-y-2">
                    {usernameHistory.history.map((entry) => (
                      <li
                        key={`${entry.username}-${entry.changed_at}`}
                        className="flex flex-wrap items-center justify-between gap-2 text-xs bg-gray-50 rounded-md px-3 py-2"
                      >
                        <span className="font-mono text-gray-700">
                          /creator/<span className="line-through text-gray-500">{entry.username}</span>
                        </span>
                        <span className="inline-flex items-center gap-1 text-gray-500">
                          <Clock className="w-3 h-3" />
                          frees up {formatRelative(entry.expires_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* My Listings */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>My Listings</CardTitle>
              <Button 
                onClick={() => navigate('/designer/upload')}
                className="bg-[#0007b5] hover:bg-[#000690]"
              >
                <Upload className="w-4 h-4 mr-2" />
                Add New Design
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {listings.length === 0 ? (
              <div className="text-center py-12">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No listings yet</h3>
                <p className="text-gray-600 mb-4">Upload your first 3D design to start selling!</p>
                <Button onClick={() => navigate('/designer/upload')}>
                  Upload Your First Design
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map((listing) => (
                  <Card key={listing.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                        <Package className="w-12 h-12 text-gray-400" />
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">{listing.title}</h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{listing.description}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Personal:</span>
                          <span className="font-semibold">${listing.personal_price}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Commercial:</span>
                          <span className="font-semibold">${listing.commercial_price}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Downloads:</span>
                          <span>{listing.download_count}</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex items-center justify-between">
                          <Badge 
                            variant={listing.status === 'active' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {listing.status}
                          </Badge>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex space-x-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => navigate(`/designer/edit/${listing.id}`)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


export const DesignerUploadRoute = () => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    use_case: '',
    personal_price: '',
    commercial_price: '',
    is_printable: true,
    tags: '',
    dimensions: '',
    dimensions_unit: 'mm',
    dimensions_unit_custom: '',
    complexity_level: ''
  });
  const [listingType, setListingType] = useState('individual'); // 'individual' or 'assembly'
  const [pricingTier, setPricingTier] = useState('premium'); // 'free' or 'premium'
  const [cadFile, setCadFile] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [hideCadFile, setHideCadFile] = useState(false);
  
  // Assembly-specific state
  const [assemblyComponents, setAssemblyComponents] = useState([]);
  const [assemblyPreview, setAssemblyPreview] = useState(null);
  const [assemblyPreviewType, setAssemblyPreviewType] = useState('image'); // 'image' or 'cad_file'
  const [showComponentFiles, setShowComponentFiles] = useState(true);
  const [showComponentNames, setShowComponentNames] = useState(true);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // State for detected units from STEP files
  const [detectedUnits, setDetectedUnits] = useState({ units: null, message: null });

  const categories = [
    'Automotive', 'Home & Living', 'Garden & Outdoor', 'Organization', 
    'Electronics', 'Toys & Games', 'Art & Decorative', 'Tools & Hardware',
    'Fashion & Jewelry', 'Replacement Parts', 'Sim Racing', 'Flight Simulator', 'Other'
  ];

  const useCases = ['Functional', 'Decorative', 'Replacement', 'Educational', 'Prototype'];

  const handleCadFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.size <= 200 * 1024 * 1024) { // 200MB limit
      setCadFile(file);
      // Reset detected units when new file is uploaded
      setDetectedUnits({ units: null, message: null });
    } else {
      alert('File size must be under 200MB');
    }
  };

  const handlePreviewImagesUpload = (event) => {
    const files = Array.from(event.target.files);
    setPreviewImages(files.slice(0, 5)); // Max 5 images
  };

  const handleAssemblyComponentsUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length < 2) {
      alert('Assembly must have at least 2 components');
      return;
    }
    if (files.length > 20) {
      alert('Maximum 20 components allowed');
      return;
    }
    
    // Create component objects with editable display names
    const components = files.map((file, index) => ({
      file: file,
      displayName: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      order: index
    }));
    setAssemblyComponents(components);
  };

  const handleAssemblyPreviewUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAssemblyPreview(file);
    }
  };

  const updateComponentName = (index, newName) => {
    const updated = [...assemblyComponents];
    updated[index].displayName = newName;
    setAssemblyComponents(updated);
  };

  const removeComponent = (index) => {
    const updated = assemblyComponents.filter((_, i) => i !== index);
    setAssemblyComponents(updated);
  };

  const moveComponent = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === assemblyComponents.length - 1) return;
    
    const updated = [...assemblyComponents];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    // Update order indices
    updated.forEach((comp, i) => comp.order = i);
    setAssemblyComponents(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    // Validate based on listing type
    if (listingType === 'individual') {
      // Individual listing needs CAD file
      if (!cadFile) {
        setSubmitStatus({
          type: 'error',
          message: 'CAD file is required for individual listings.'
        });
        return;
      }

      // Validate images are required if CAD file is hidden
      if (hideCadFile && previewImages.length === 0) {
        setSubmitStatus({
          type: 'error',
          message: 'Preview images are required when CAD file is hidden from the listing.'
        });
        return;
      }
    } else {
      // Assembly listing needs components and preview
      if (assemblyComponents.length < 2) {
        setSubmitStatus({
          type: 'error',
          message: 'Assembly must have at least 2 component files.'
        });
        return;
      }

      if (!assemblyPreview) {
        setSubmitStatus({
          type: 'error',
          message: 'Assembly preview (image or CAD file) is required.'
        });
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Use proper backend URL from environment variable
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      const listingData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        use_case: formData.use_case,
        pricing_tier: pricingTier,
        personal_price: parseFloat(formData.personal_price),
        commercial_price: parseFloat(formData.commercial_price),
        print_markup_percentage: 0.0,
        is_printable: formData.is_printable,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        dimensions: formData.dimensions,
        dimensions_unit: formData.dimensions_unit === 'other' ? formData.dimensions_unit_custom : formData.dimensions_unit,
        complexity_level: formData.complexity_level,
        preview_type: hideCadFile ? 'images' : (previewImages.length > 0 ? 'images' : '3d_model'),
        hide_cad_file: hideCadFile,
        listing_type: listingType,
        assembly_preview_type: listingType === 'assembly' ? assemblyPreviewType : 'none',
        show_component_files: showComponentFiles,
        show_component_names: showComponentNames
      };

      // Step 1: Create the listing
      const response = await fetch(`${API_BASE}/api/marketplace/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(listingData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Failed to create listing');
      }

      const listingId = result.listing_id;
      let uploadErrors = [];

      // Step 2: Upload CAD file if provided
      if (cadFile) {
        try {
          const cadFormData = new FormData();
          cadFormData.append('file', cadFile);

          const cadResponse = await fetch(`${API_BASE}/api/marketplace/upload-cad-file?listing_id=${listingId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: cadFormData
          });

          if (!cadResponse.ok) {
            const cadError = await cadResponse.json();
            uploadErrors.push(`CAD file upload failed: ${cadError.detail || 'Unknown error'}`);
          }
        } catch (error) {
          uploadErrors.push(`CAD file upload error: ${error.message}`);
        }
      }

      // Step 3: Upload assembly components if it's an assembly
      if (listingType === 'assembly' && assemblyComponents.length > 0) {
        try {
          const componentFormData = new FormData();
          assemblyComponents.forEach(comp => {
            componentFormData.append('files', comp.file);
          });
          
          // Pass component display names as JSON
          const displayNames = assemblyComponents.map(comp => comp.displayName);
          componentFormData.append('component_names', JSON.stringify(displayNames));
          componentFormData.append('listing_id', listingId);

          const componentResponse = await fetch(`${API_BASE}/api/marketplace/upload-assembly-components`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: componentFormData
          });

          if (!componentResponse.ok) {
            const componentError = await componentResponse.json();
            uploadErrors.push(`Assembly components upload failed: ${componentError.detail || 'Unknown error'}`);
          }
        } catch (error) {
          uploadErrors.push(`Assembly components upload error: ${error.message}`);
        }
      }

      // Step 4: Upload assembly preview if provided
      if (listingType === 'assembly' && assemblyPreview) {
        try {
          const previewFormData = new FormData();
          previewFormData.append('file', assemblyPreview);
          previewFormData.append('listing_id', listingId);
          previewFormData.append('preview_type', assemblyPreviewType);

          const previewResponse = await fetch(`${API_BASE}/api/marketplace/upload-assembly-preview`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: previewFormData
          });

          if (!previewResponse.ok) {
            const previewError = await previewResponse.json();
            uploadErrors.push(`Assembly preview upload failed: ${previewError.detail || 'Unknown error'}`);
          }
        } catch (error) {
          uploadErrors.push(`Assembly preview upload error: ${error.message}`);
        }
      }

      // Step 5: Upload preview images if provided (for individual listings)
      if (previewImages.length > 0) {
        try {
          const imageFormData = new FormData();
          previewImages.forEach(file => {
            imageFormData.append('files', file);
          });

          const imageResponse = await fetch(`${API_BASE}/api/marketplace/upload-preview-images?listing_id=${listingId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: imageFormData
          });

          if (!imageResponse.ok) {
            const imageError = await imageResponse.json();
            uploadErrors.push(`Preview images upload failed: ${imageError.detail || 'Unknown error'}`);
          }
        } catch (error) {
          uploadErrors.push(`Preview images upload error: ${error.message}`);
        }
      }

      // Show success or partial success message
      if (uploadErrors.length === 0) {
        // Track design upload in Google Analytics
        trackDesignUpload({
          title: formData.title,
          category: formData.category,
          is_printable: formData.is_printable
        });
        
        setSubmitStatus({
          type: 'success',
          message: 'Design uploaded successfully! It will be reviewed and published within 24 hours.'
        });
      } else {
        setSubmitStatus({
          type: 'warning',
          message: `Listing created but some files failed to upload: ${uploadErrors.join(', ')}. You can try uploading files again later.`
        });
      }

      // Reset form on success
      setFormData({
        title: '', description: '', category: '', use_case: '',
        personal_price: '', commercial_price: '',
        is_printable: true, tags: '', dimensions: '', 
        dimensions_unit: 'mm', dimensions_unit_custom: '', complexity_level: ''
      });
      setCadFile(null);
      setPreviewImages([]);
      setAssemblyComponents([]);
      setAssemblyPreview(null);
      setListingType('individual');
      setPricingTier('premium');
      setDetectedUnits({ units: null, message: null });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to upload design. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center p-6">
            <h2 className="text-xl font-semibold mb-4">Access Required</h2>
            <p className="text-gray-600 mb-4">Please sign in to upload designs.</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MainNavigation 
        onNavigate={(page) => {
          if (page === 'home') navigate('/');
          else if (page === 'designer-dashboard') navigate('/designer/dashboard');
          else navigate(`/${page}`);
        }} 
        currentPage="designer-upload" 
        cartCount={0}
      />

      {/* Upload Action Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Upload New Design</h1>
            </div>
            <Button onClick={() => navigate('/designer/dashboard')} variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Add New Marketplace Listing</CardTitle>
            <CardDescription>
              Upload your 3D design with details and pricing to sell in the marketplace.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Listing Type Selector */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <Label className="text-base font-semibold mb-3 block">Listing Type *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setListingType('individual')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      listingType === 'individual' 
                        ? 'border-blue-500 bg-blue-100' 
                        : 'border-gray-300 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="font-semibold text-lg mb-1">Individual Part</div>
                    <div className="text-sm text-gray-600">Single 3D file for one component or object</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setListingType('assembly')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      listingType === 'assembly' 
                        ? 'border-blue-500 bg-blue-100' 
                        : 'border-gray-300 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="font-semibold text-lg mb-1">Assembly</div>
                    <div className="text-sm text-gray-600">Multiple parts that form a complete product</div>
                  </button>
                </div>
              </div>

              {/* Pricing Tier Selector */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <Label className="text-base font-semibold mb-3 block">Pricing Tier *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setPricingTier('free')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      pricingTier === 'free' 
                        ? 'border-green-500 bg-green-100' 
                        : 'border-gray-300 bg-white hover:border-green-300'
                    }`}
                  >
                    <div className="font-semibold text-lg mb-1">Free</div>
                    <div className="text-sm text-gray-600">Available for free download</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPricingTier('premium')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      pricingTier === 'premium' 
                        ? 'border-green-500 bg-green-100' 
                        : 'border-gray-300 bg-white hover:border-green-300'
                    }`}
                  >
                    <div className="font-semibold text-lg mb-1">Premium</div>
                    <div className="text-sm text-gray-600">Paid design with licensing options</div>
                  </button>
                </div>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="title">Design Title *</Label>
                  <Input
                    id="title"
                    type="text"
                    placeholder="Modern Desk Organizer"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({...prev, category: value}))} 
                    required
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your design, its features, recommended use cases, and printing requirements..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  required
                  rows={4}
                  className="mt-2"
                />
              </div>

              {/* File Uploads Section */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">File Uploads</h3>
                
                {/* Individual Part File Upload */}
                {listingType === 'individual' && (
                  <>
                    <div className="mb-6">
                      <Label htmlFor="cad_file">3D CAD File (Required - Max 200MB) *</Label>
                      <Input
                        id="cad_file"
                        type="file"
                        accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                        onChange={handleCadFileUpload}
                        className="mt-2"
                        required
                      />
                      <p className="text-xs text-slate-600 mt-1">
                        CAD file is required for manufacturing and print cost calculations
                      </p>
                      {cadFile && (
                        <Badge className="mt-2 bg-green-50 text-green-700 border-green-200">
                          ✓ {cadFile.name}
                        </Badge>
                      )}
                    </div>

                    <div className="mb-6 bg-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="hide_cad_file"
                          checked={hideCadFile}
                          onChange={(e) => setHideCadFile(e.target.checked)}
                          className="w-4 h-4 text-[#0007b5] border-gray-300 rounded focus:ring-blue-500 mt-1"
                        />
                        <div className="ml-3">
                          <label htmlFor="hide_cad_file" className="font-medium text-slate-900 cursor-pointer">
                            Hide CAD file from listing
                          </label>
                          <p className="text-xs text-slate-600 mt-1">
                            Check this if you don't want customers to see the 3D preview. 
                            <strong className="text-orange-600"> If checked, preview images are required.</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="preview_images">
                        Preview Images {hideCadFile ? '(Required - Max 5) *' : '(Optional - Max 5)'}
                      </Label>
                      <Input
                        id="preview_images"
                        type="file"
                        accept="image/*"
                        onChange={handlePreviewImagesUpload}
                        multiple
                        className="mt-2"
                        required={hideCadFile}
                      />
                      <p className="text-xs text-slate-600 mt-1">
                        {hideCadFile 
                          ? 'Images required when CAD file is hidden'
                          : 'Upload images to complement the 3D preview'
                        }
                      </p>
                      {previewImages.length > 0 && (
                        <div className="mt-2 space-x-2">
                          {previewImages.map((file, index) => (
                            <Badge key={index} className="bg-blue-50 text-blue-700 border-blue-200">
                              {file.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {cadFile && !hideCadFile && (
                      <div className="mt-6">
                        <Label>3D Model Preview</Label>
                        <div className="mt-2 border rounded-lg p-4 bg-white">
                          <ThreeDPreview 
                            file={cadFile} 
                            onAnalysis={(analysisData) => {
                              if (analysisData.dimensions) {
                                setFormData(prev => ({
                                  ...prev,
                                  dimensions: `${analysisData.dimensions.x} x ${analysisData.dimensions.y} x ${analysisData.dimensions.z}`,
                                  dimensions_unit: analysisData.dimensions.unit || 'mm'
                                }));
                              }
                              // Update detected units if available from STEP conversion
                              if (analysisData.detectedUnits) {
                                setDetectedUnits({
                                  units: analysisData.detectedUnits.units,
                                  message: analysisData.detectedUnits.message
                                });
                                // Auto-set dimensions unit to detected units
                                if (analysisData.detectedUnits.units && analysisData.detectedUnits.units !== 'unknown') {
                                  setFormData(prev => ({
                                    ...prev,
                                    dimensions_unit: analysisData.detectedUnits.units
                                  }));
                                }
                              } else {
                                setDetectedUnits({ units: null, message: null });
                              }
                            }}
                            className="w-full"
                          />
                          
                          {/* Display detected units from STEP file */}
                          {detectedUnits.units && detectedUnits.units !== 'unknown' && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <p className="text-sm text-blue-800">
                                <strong>Detected from file:</strong> {detectedUnits.units === 'mm' ? 'Millimeters (mm)' : 
                                  detectedUnits.units === 'cm' ? 'Centimeters (cm)' : 
                                  detectedUnits.units === 'm' ? 'Meters (m)' : 
                                  detectedUnits.units === 'in' ? 'Inches (in)' : 
                                  detectedUnits.units === 'ft' ? 'Feet (ft)' : detectedUnits.units}
                              </p>
                              <p className="text-xs text-blue-600 mt-1">{detectedUnits.message}</p>
                            </div>
                          )}
                          {detectedUnits.units === 'unknown' && detectedUnits.message && (
                            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-xs text-yellow-700">{detectedUnits.message}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Assembly File Upload */}
                {listingType === 'assembly' && (
                  <>
                    <div className="mb-6">
                      <Label htmlFor="assembly_components">Assembly Component Files (2-20 files, Max 100MB each) *</Label>
                      <Input
                        id="assembly_components"
                        type="file"
                        accept=".stl,.obj,.3mf,.ply,.step,.stp"
                        onChange={handleAssemblyComponentsUpload}
                        multiple
                        className="mt-2"
                        required
                      />
                      <p className="text-xs text-slate-600 mt-1">
                        Upload all CAD files that make up your assembly (minimum 2 parts required)
                      </p>
                    </div>

                    {assemblyComponents.length > 0 && (
                      <div className="mb-6 border rounded-lg p-4 bg-gray-50">
                        <Label className="mb-3 block">Component Files ({assemblyComponents.length})</Label>
                        <div className="space-y-2">
                          {assemblyComponents.map((comp, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border">
                              <span className="text-sm font-mono text-gray-600">{index + 1}.</span>
                              <Input
                                value={comp.displayName}
                                onChange={(e) => updateComponentName(index, e.target.value)}
                                placeholder="Component name"
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => moveComponent(index, 'up')}
                                disabled={index === 0}
                              >
                                ↑
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => moveComponent(index, 'down')}
                                disabled={index === assemblyComponents.length - 1}
                              >
                                ↓
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => removeComponent(index)}
                                className="text-red-600"
                              >
                                ✕
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mb-6">
                      <Label htmlFor="assembly_preview">Assembly Preview (Image or 3D CAD file) *</Label>
                      <div className="mt-2 space-y-2">
                        <div className="flex gap-4 mb-2">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="image"
                              checked={assemblyPreviewType === 'image'}
                              onChange={(e) => setAssemblyPreviewType(e.target.value)}
                              className="mr-2"
                            />
                            Image
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value="cad_file"
                              checked={assemblyPreviewType === 'cad_file'}
                              onChange={(e) => setAssemblyPreviewType(e.target.value)}
                              className="mr-2"
                            />
                            3D CAD File
                          </label>
                        </div>
                        <Input
                          id="assembly_preview"
                          type="file"
                          accept={assemblyPreviewType === 'image' ? 'image/*' : '.stl,.obj,.3mf,.step,.stp'}
                          onChange={handleAssemblyPreviewUpload}
                          className="mt-2"
                          required
                        />
                        <p className="text-xs text-slate-600">
                          {assemblyPreviewType === 'image' 
                            ? 'Upload an image showing the complete assembly'
                            : 'Upload a 3D CAD file of the complete assembled product'
                          }
                        </p>
                        {assemblyPreview && (
                          <Badge className="mt-2 bg-green-50 text-green-700 border-green-200">
                            ✓ {assemblyPreview.name}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="mb-6 bg-white rounded-lg p-4 border border-blue-200 space-y-3">
                      <Label className="block font-semibold">Component Visibility Settings</Label>
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="show_component_files"
                          checked={showComponentFiles}
                          onChange={(e) => setShowComponentFiles(e.target.checked)}
                          className="w-4 h-4 text-[#0007b5] border-gray-300 rounded focus:ring-blue-500 mt-1"
                        />
                        <div className="ml-3">
                          <label htmlFor="show_component_files" className="font-medium text-slate-900 cursor-pointer">
                            Show individual component 3D previews
                          </label>
                          <p className="text-xs text-slate-600 mt-1">
                            Allow customers to view each component's 3D model individually
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="show_component_names"
                          checked={showComponentNames}
                          onChange={(e) => setShowComponentNames(e.target.checked)}
                          className="w-4 h-4 text-[#0007b5] border-gray-300 rounded focus:ring-blue-500 mt-1"
                        />
                        <div className="ml-3">
                          <label htmlFor="show_component_names" className="font-medium text-slate-900 cursor-pointer">
                            Show component names in listing
                          </label>
                          <p className="text-xs text-slate-600 mt-1">
                            Display the list of component names to customers
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* File Requirements Notice */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                  <div className="flex items-start">
                    <div className="text-yellow-600 mr-2">⚠️</div>
                    <div className="text-sm text-yellow-800">
                      <strong>File Requirements:</strong>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>CAD file is always required</li>
                        <li>If you hide the CAD file, preview images become required</li>
                        <li>If you don't hide the CAD file, customers will see interactive 3D preview</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Pricing & Licensing</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="personal_price">Personal Use Price *</Label>
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="personal_price"
                        type="number"
                        step="0.01"
                        min="1"
                        placeholder="9.99"
                        value={formData.personal_price}
                        onChange={(e) => setFormData(prev => ({...prev, personal_price: e.target.value}))}
                        className="pl-8"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="commercial_price">Commercial Use Price *</Label>
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="commercial_price"
                        type="number"
                        step="0.01"
                        min="1"
                        placeholder="49.99"
                        value={formData.commercial_price}
                        onChange={(e) => setFormData(prev => ({...prev, commercial_price: e.target.value}))}
                        className="pl-8"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="use_case">Use Case *</Label>
                  <Select 
                    value={formData.use_case} 
                    onValueChange={(value) => setFormData(prev => ({...prev, use_case: value}))} 
                    required
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose use case" />
                    </SelectTrigger>
                    <SelectContent>
                      {useCases.map(useCase => (
                        <SelectItem key={useCase} value={useCase}>{useCase}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="dimensions">Dimensions (Optional)</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="dimensions"
                      type="text"
                      placeholder="120 x 80 x 15"
                      value={formData.dimensions}
                      onChange={(e) => setFormData(prev => ({...prev, dimensions: e.target.value}))}
                      className="flex-1"
                    />
                    <Select 
                      value={formData.dimensions_unit} 
                      onValueChange={(value) => setFormData(prev => ({...prev, dimensions_unit: value}))}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mm">mm</SelectItem>
                        <SelectItem value="cm">cm</SelectItem>
                        <SelectItem value="in">in</SelectItem>
                        <SelectItem value="ft">ft</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.dimensions_unit === 'other' && (
                    <Input
                      type="text"
                      placeholder="Enter custom unit"
                      value={formData.dimensions_unit_custom}
                      onChange={(e) => setFormData(prev => ({...prev, dimensions_unit_custom: e.target.value}))}
                      className="mt-2"
                    />
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="tags">Tags (Comma separated)</Label>
                <Input
                  id="tags"
                  type="text"
                  placeholder="organizer, desk, office, storage"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({...prev, tags: e.target.value}))}
                  className="mt-2"
                />
              </div>

              {submitStatus && (
                <div className={`rounded-lg p-4 ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : submitStatus.type === 'info'
                    ? 'bg-blue-50 text-blue-800 border border-blue-200'
                    : submitStatus.type === 'warning'
                    ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-[#0007b5] hover:bg-[#000690] py-3 text-lg font-semibold"
              >
                {isSubmitting ? 'Uploading Design...' : 'Upload Design to Marketplace'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


export const DesignerTermsRoute = () => {
  const navigate = useNavigate();
  
  return (
    <SharedPageLayout
      navigateHome={() => navigate('/')}
      navigateToCart={() => navigate('/cart')}
      navigateToProfile={() => navigate('/profile')}
      cartCount={0}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Designer Terms & Conditions</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-red-900 mb-3">⚠️ Important Legal Notice</h2>
            <p className="text-red-800">
              By uploading designs to our marketplace, you acknowledge and agree to the terms below. 
              Please read carefully before proceeding.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Intellectual Property Ownership</h2>
            <p className="text-slate-700 mb-4">
              You represent and warrant that you are the sole owner of all intellectual property rights 
              in the designs you upload, or you have obtained all necessary permissions, licenses, and 
              authorizations to use, distribute, and commercialize such designs.
            </p>
            <p className="text-slate-700 mb-4">
              You retain ownership of your original designs. By uploading to our platform, you grant 
              Create 3D Works a non-exclusive license to display, distribute, and facilitate the sale 
              of your designs through our marketplace.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Content Liability & Indemnification</h2>
            <p className="text-slate-700 mb-4">
              <strong>You agree to indemnify and hold harmless Create 3D Works, its officers, directors, 
              employees, and agents from and against any and all claims, damages, obligations, losses, 
              liabilities, costs, and expenses arising from:</strong>
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Your uploaded designs or any content you provide</li>
              <li>Your breach of these terms or violation of any law or regulation</li>
              <li>Any claim that your designs infringe on third-party intellectual property rights</li>
              <li>Any damages or losses resulting from the use of your designs by customers</li>
              <li>Any defects, errors, or safety issues in your uploaded designs</li>
            </ul>
            <p className="text-slate-700 mb-4">
              Create 3D Works assumes no responsibility for the content, accuracy, or legal status 
              of designer-uploaded materials.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Content Standards & Restrictions</h2>
            <p className="text-slate-700 mb-4">Uploaded designs must not:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Infringe on any third-party intellectual property rights</li>
              <li>Contain illegal, harmful, or offensive content</li>
              <li>Be intended for weapons, illegal activities, or harmful purposes</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Contain malicious code or corrupted data</li>
              <li>Be false, misleading, or deceptive in description or function</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibent text-slate-900 mb-4">4. Revenue Sharing & Payments</h2>
            <p className="text-slate-700 mb-4">
              Revenue sharing percentages and payment terms will be communicated separately upon 
              designer approval. Create 3D Works reserves the right to modify revenue sharing terms 
              with 30 days written notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Limitation of Liability</h2>
            <p className="text-slate-700 mb-4">
              Create 3D Works shall not be liable for any indirect, incidental, special, consequential, 
              or punitive damages arising from or related to your use of the marketplace platform or 
              any customer's use of your designs.
            </p>
            <p className="text-slate-700 mb-4">
              Our total liability shall not exceed the total amount paid to you in the preceding 12 months.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Platform Rights</h2>
            <p className="text-slate-700 mb-4">
              Create 3D Works reserves the right to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Review and approve or reject any submitted designs</li>
              <li>Remove designs that violate these terms</li>
              <li>Suspend or terminate designer accounts for violations</li>
              <li>Modify platform features and functionality</li>
              <li>Update these terms with reasonable notice</li>
            </ul>
          </section>

          <div className="bg-blue-50 rounded-lg p-6 mt-8">
            <p className="text-blue-800 text-sm">
              <strong>By proceeding with your designer application, you acknowledge that you have read, 
              understood, and agree to be bound by these terms and conditions.</strong>
            </p>
          </div>
        </div>
      </div>
    </SharedPageLayout>
  );
};


export const CustomerLicenseTermsRoute = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white">
      <MainNavigation 
        onNavigate={(page) => {
          if (page === 'home') navigate('/');
          else if (page === 'marketplace') navigate('/marketplace');
          else navigate(`/${page}`);
        }} 
        currentPage="license-terms" 
        cartCount={0}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Design License Terms</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-blue-900 mb-3">📄 License Summary</h2>
            <p className="text-blue-800">
              When you purchase a design from our marketplace, you receive a license to use the design 
              according to the terms below. This is not a transfer of ownership.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. License Grant</h2>
            <p className="text-slate-700 mb-4">
              Upon successful purchase, you are granted a <strong>non-exclusive, non-transferable license</strong> 
              to use the purchased 3D design file according to the license type you selected:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="font-semibold text-green-900 mb-2">Personal Use License</h3>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>• Personal projects and hobbies</li>
                  <li>• Non-commercial use only</li>
                  <li>• Cannot resell printed items</li>
                  <li>• Cannot redistribute design files</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2">Commercial Use License</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Commercial and business use</li>
                  <li>• Can sell printed items</li>
                  <li>• Cannot resell or redistribute design files</li>
                  <li>• Cannot sublicense to others</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Restrictions</h2>
            <p className="text-slate-700 mb-4">You may NOT:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Resell, redistribute, or share the design files with others</li>
              <li>Reverse engineer or create derivative works without permission</li>
              <li>Use the designs for illegal or harmful purposes</li>
              <li>Claim ownership or authorship of the designs</li>
              <li>Transfer your license to another person or entity</li>
              <li>Upload the designs to other marketplaces or platforms</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Design Ownership</h2>
            <p className="text-slate-700 mb-4">
              All designs remain the intellectual property of their original creators (designers). 
              Your purchase grants you usage rights only, not ownership rights. The designer retains 
              all copyrights, patents, and other intellectual property rights in the designs.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Download & Access</h2>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Downloads are available immediately after successful payment</li>
              <li>Unlimited downloads of purchased designs for licensed use</li>
              <li>Files are provided "as-is" without warranty</li>
              <li>You are responsible for ensuring compatibility with your equipment</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibent text-slate-900 mb-4">5. Liability Disclaimer</h2>
            <p className="text-slate-700 mb-4">
              Create 3D Works provides designs "as-is" without any warranty. We are not responsible for:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Print quality or dimensional accuracy</li>
              <li>Material compatibility or performance</li>
              <li>Fitness for any particular purpose</li>
              <li>Safety or structural integrity of printed items</li>
              <li>Any damages resulting from use of the designs</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Refund Policy</h2>
            <p className="text-slate-700 mb-4">
              Due to the digital nature of design files, all sales are final. Refunds may be considered 
              only in cases of technical issues preventing download or fundamental errors in the design file.
            </p>
          </section>

          <div className="bg-slate-100 rounded-lg p-6 mt-8">
            <p className="text-slate-700 text-sm">
              <strong>By purchasing and downloading designs from our marketplace, you acknowledge that you have 
              read, understood, and agree to be bound by these license terms.</strong>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
